import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Calendar, MapPin, Check } from "lucide-react";
import { api } from "@/lib/api";

/**
 * `/comp/:token` — comp-ticket landing.
 *
 *   100%-off tokens take a direct "fill in your details" path
 *   (POST /api/checkout/comp-claim) — no Stripe roundtrip, ticket is
 *   issued immediately and the confirmation email is fired server-side.
 *
 *   Partial-discount tokens (<100%) keep the original Stripe Checkout
 *   flow (POST /api/checkout/comp).
 */
export default function CompCheckout() {
  const { token } = useParams();
  const [data, setData] = useState(null);   // GET /api/comp/<token>
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [claimed, setClaimed] = useState(null);  // success payload from /comp-claim

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/comp/${token}`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Invalid comp link");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const isFreebie = data?.discount_percent === 100;

  const onClaimFreebie = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    if (!name.trim() || !email.trim()) {
      setError("Please fill in your name and email.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const { data: resp } = await api.post("/checkout/comp-claim", {
        token,
        name: name.trim(),
        email: email.trim(),
      });
      setClaimed(resp);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not claim your ticket. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const onContinuePaid = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data: resp } = await api.post("/checkout/comp", {
        token,
        email: email.trim() || undefined,
        origin_url: window.location.origin,
      });
      if (resp?.url) {
        window.location.href = resp.url;
      } else {
        setError("Could not start checkout. Please try again.");
        setSubmitting(false);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  if (error && !data) {
    return (
      <main className="min-h-screen bg-[var(--mm-bg,#050a1a)] text-white flex items-center justify-center p-6" data-testid="comp-error-page">
        <div className="max-w-md w-full border border-rose-400/40 text-rose-200 px-6 py-8 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.32em] mb-2">Link error</p>
          <p className="text-[14px] opacity-90">{error}</p>
          <Link to="/tickets" className="btn-line inline-flex mt-6 text-sm" data-testid="comp-error-shows">View all shows</Link>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="min-h-screen bg-[var(--mm-bg,#050a1a)] text-white flex items-center justify-center" data-testid="comp-loading">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/55">Loading comp ticket…</p>
      </main>
    );
  }

  const consumed = data.status === "consumed";
  const isArtist = data.tier === "artist";

  return (
    <main className="min-h-screen bg-[var(--mm-bg,#050a1a)] text-white" data-testid="comp-checkout-page">
      <div className="max-w-md mx-auto px-6 sm:px-10 py-14 sm:py-20">
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/55 mb-2 text-center">
          {isFreebie ? "Complimentary ticket" : `Personal invite \u2014 ${data.discount_percent}% off`}
        </p>
        <h1
          className="deco-text text-4xl sm:text-5xl uppercase text-center leading-[0.95] mb-2"
          style={{ letterSpacing: "0.1em" }}
        >
          Metamorph
        </h1>
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/45 text-center mb-10">
          {data.city_label}
        </p>

        <div className="border border-white/15 p-6 mb-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/50 mb-1">
            {isFreebie
              ? (isArtist ? "Artist \u00b7 Complimentary ticket" : "Complimentary ticket")
              : (isArtist ? "Artist \u00b7 Comp ticket" : "Comp ticket")}
          </p>
          <h2
            className="text-2xl mb-3"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
          >
            {data.city_label}
          </h2>
          {data.date && (
            <p className="flex items-center gap-1.5 text-[12px] text-white/70 mb-1">
              <Calendar className="w-3 h-3" strokeWidth={1.4} /> {data.date}
            </p>
          )}
          {data.venue && (
            <p className="flex items-center gap-1.5 text-[12px] text-white/55 mb-1">
              <MapPin className="w-3 h-3" strokeWidth={1.4} /> {data.venue}
            </p>
          )}

          {/* Price block — only rendered when there's an actual price to surface. */}
          {!isFreebie && (
            <div className="border-t border-white/15 mt-4 pt-4">
              <div className="flex items-baseline justify-between mb-1">
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/55">Full price</span>
                <span className="tabular-nums line-through text-white/45 text-sm">
                  {data.currency_symbol}{Number(data.price_full).toFixed(2)}
                </span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--mm-accent)" }}>
                  Your price
                </span>
                <span
                  className="tabular-nums"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", fontSize: "30px", fontWeight: 500, letterSpacing: "0.01em" }}
                  data-testid="comp-price"
                >
                  {data.currency_symbol}{Number(data.price_comp).toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>

        {consumed ? (
          <div
            className="border border-white/25 px-5 py-5 text-center mb-3"
            data-testid="comp-already-used"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/65 mb-1">
              Already redeemed
            </p>
            <p className="text-[13px] text-white/75 leading-relaxed">
              This invitation has been used. Reach out to George if you think this is an error.
            </p>
            <Link to="/tickets" className="btn-line inline-flex mt-5 text-xs" data-testid="comp-view-shows">
              View all shows <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.4} />
            </Link>
          </div>
        ) : claimed ? (
          <div
            className="border border-emerald-400/40 px-5 py-6 text-center"
            data-testid="comp-claim-success"
          >
            <div className="flex justify-center mb-3">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-emerald-400/60 text-emerald-200">
                <Check className="w-5 h-5" strokeWidth={1.6} />
              </span>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-200/85 mb-2">
              Ticket confirmed
            </p>
            <p className="text-[14px] text-white/85 leading-relaxed mb-4">
              We&rsquo;ve sent your ticket to <span className="text-white">{claimed.email}</span>. Look out for the PDF + QR code from <em>hello@metamorph-george-maple.com</em>.
            </p>
            <p className="font-mono text-[11px] text-white/55 tracking-wider mb-1">Reference</p>
            <p
              className="font-mono text-[18px] tracking-[0.18em]"
              style={{ color: "#f3e0a6" }}
              data-testid="comp-claim-reference"
            >
              {claimed.ticket_reference}
            </p>
            <Link to="/" className="btn-line inline-flex mt-6 text-xs" data-testid="comp-claim-home">
              Back to Metamorph <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.4} />
            </Link>
          </div>
        ) : isFreebie ? (
          <form onSubmit={onClaimFreebie}>
            <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/65 text-center mb-4">
              Fill in your details
            </p>
            <input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
              className="w-full bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-4 py-3 text-sm text-white font-mono tracking-wide placeholder-white/40 mb-3"
              data-testid="comp-name-input"
            />
            <input
              type="email"
              placeholder="Email for your ticket"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-4 py-3 text-sm text-white font-mono tracking-wide placeholder-white/40 mb-3"
              data-testid="comp-email-input"
            />
            <button
              type="submit"
              disabled={submitting}
              className="btn-line w-full justify-center text-sm py-3.5 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="comp-claim-submit"
            >
              {submitting ? "Claiming…" : (
                <>
                  Claim your ticket <ArrowRight className="w-4 h-4" strokeWidth={1.4} />
                </>
              )}
            </button>
            {error && (
              <p className="font-mono text-[10px] text-red-400/85 text-center mt-3" data-testid="comp-error">
                {error}
              </p>
            )}
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40 text-center mt-5">
              Complimentary ticket · PDF + QR sent to your email
            </p>
          </form>
        ) : (
          <>
            <input
              type="email"
              placeholder="Email for your ticket (optional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border border-white/25 focus:border-[var(--mm-accent)] outline-none px-4 py-3 text-sm text-white font-mono tracking-wide placeholder-white/40 mb-3"
              data-testid="comp-email"
            />
            <button
              onClick={onContinuePaid}
              disabled={submitting}
              className="btn-line w-full justify-center text-sm py-3.5 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="comp-continue"
            >
              {submitting ? "Opening Stripe…" : (
                <>
                  Continue to payment <ArrowRight className="w-4 h-4" strokeWidth={1.4} />
                </>
              )}
            </button>
            {error && (
              <p className="font-mono text-[10px] text-red-400/85 text-center mt-3" data-testid="comp-error">
                {error}
              </p>
            )}
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40 text-center mt-5">
              Single-use link · payable via Stripe
            </p>
          </>
        )}

        <p className="text-center mt-12 font-mono text-[10px] uppercase tracking-[0.32em] text-white/40">
          <Link to="/" className="hover:text-white/85">← Back to Metamorph</Link>
        </p>
      </div>
    </main>
  );
}
